import React from 'react';

import { useForm } from 'react-hook-form';
import { LbSelect } from '@infoods/lifebase-ui';
import AddRoundedIcon from '@mui/icons-material/AddRounded';

import classes from './AddMicroNutrientModal.module.scss';
import { LbNumericFormatInput } from '../../../../../shared/components/form-components';
import { LbModal } from '../../../../../shared/components/modals';
import { MicroNutrient } from '../../../../interfaces/MicroNutrient';
import { MicroNutrientOption } from '../../../../interfaces/MicroNutrientOption';
import { NUTRIENT_ROUNDING_PRECISION } from '../../constants/foodFormNutrients';

type AddMicroNutrientModalProps = {
  /** never empty - the card disables "Add nutrient" when nothing is left */
  options: MicroNutrientOption[];
  onAdd: (microNutrient: MicroNutrient) => void;
  onClose: () => void;
};

type AddMicroNutrientFormValues = {
  id: number;
  measurement: string;
  amount: number | null;
};

const MEASUREMENTS = ["IU's", 'mcg', 'mg'];
const measurementOptions = MEASUREMENTS.map((value) => ({ value, label: value }));
const rangeMessage = 'Value should be between 0 and 9999';

const AddMicroNutrientModal = ({
  options,
  onAdd,
  onClose,
}: AddMicroNutrientModalProps) => {
  const { control, handleSubmit } = useForm<AddMicroNutrientFormValues>({
    defaultValues: { id: options[0]?.id, measurement: MEASUREMENTS[0], amount: null },
    mode: 'onTouched',
  });
  const nameOptions = options.map(({ id, name }) => ({ value: id, label: name }));

  const handleAdd = handleSubmit(({ id, measurement, amount }) => {
    const option = options.find((item) => item.id === id);

    if (option) {
      onAdd({ id, name: option.name, measurement, amount: Number(amount) });
      onClose();
    }
  });

  // no <form>: the modal renders inside the food <form> tree, where a submit event would bubble to it
  return (
    <LbModal
      open
      title="Add New Micro Nutrient"
      onClose={onClose}
      primaryBtnConfig={{
        text: 'Add',
        startIcon: <AddRoundedIcon />,
        onClick: handleAdd,
      }}
      secondaryBtnConfig={{ text: 'Cancel', onClick: onClose }}
    >
      <div className={classes.fields}>
        <LbSelect
          control={control}
          name="id"
          label="Micro Nutrient Name"
          options={nameOptions}
          rules={{ required: 'Micro Nutrient Name is required' }}
        />
        <LbSelect
          control={control}
          name="measurement"
          label="Measurement"
          options={measurementOptions}
          rules={{ required: 'Measurement is required' }}
        />
        <LbNumericFormatInput
          control={control}
          name="amount"
          label="Quantity"
          rules={{
            required: 'Quantity is required',
            min: { value: 0, message: rangeMessage },
            max: { value: 9999, message: rangeMessage },
          }}
          allowNegative={false}
          decimalScale={NUTRIENT_ROUNDING_PRECISION}
          selectValueOnFocus
        />
      </div>
    </LbModal>
  );
};

export { AddMicroNutrientModal };
